import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Drawer from '../ui/Drawer';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import ConfirmDialog from '../ui/ConfirmDialog';
import FieldError from '../ui/FieldError';
import { ApiError } from '../../api/client';
import { stationsApi } from '../../api/stations';
import { getCurrentUser } from '../../auth/session';
import StationPicker from './StationPicker';
import { formatPrice, groupOptions, optionName, unitShort } from './stationAdapter';
import { mapServerErrors, PRICE_MAX, parsePrice, validatePlaceId, validatePrice } from './priceForm';

const field = 'w-full h-10 px-3 rounded-lg text-[13px] text-on-surface border outline-none transition';
const fieldOk = 'bg-surface-container-low border-outline-variant/30 focus:bg-surface-container-lowest focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]';
const fieldBad = 'bg-status-alert-bg/40 border-error focus:shadow-[0_0_0_3px_rgba(186,26,26,0.15)]';
const readOnlyField = `${field} bg-surface-container-low/60 border-transparent text-on-surface-variant`;

const errorMessage = (err) => (err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');

/**
 * Add / Edit Fuel Price drawer.
 * mode 'add': pick a station (Google or Place ID); mode 'edit': `station` is fixed.
 * `initialFuelOptionId` preselects a fuel (e.g. the chip that was clicked).
 * `onSaved(placeId, fuelsWithEntries)` lets the page patch the row before refetching.
 */
export default function FuelPriceDrawer({ mode, station, initialFuelOptionId, options, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const groups = useMemo(() => groupOptions(options), [options]);
  const activeOptions = useMemo(() => groups.flatMap((g) => g.options), [groups]);

  const [target, setTarget] = useState(isEdit ? { placeId: station.placeId, name: station.name, address: station.address } : null);
  const [fuelOptionId, setFuelOptionId] = useState(() => {
    if (initialFuelOptionId) return initialFuelOptionId;
    if (isEdit) return station.latestPrice?.fuelOptionId ?? station.fuels[0]?.fuelOptionId ?? activeOptions[0]?.id ?? null;
    return activeOptions[0]?.id ?? null;
  });
  const [price, setPrice] = useState('');
  const [priceTouched, setPriceTouched] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState({});
  const [general, setGeneral] = useState([]);
  const [attempted, setAttempted] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'reset' | 'discard'
  const priceRef = useRef(null);
  const busy = useRef(false);

  const option = activeOptions.find((o) => o.id === fuelOptionId) ?? null;
  const category = option?.category ?? groups[0]?.key ?? '';

  // Current values at this station, to label options and prefill the price
  const placeIdValid = target && !validatePlaceId(target.placeId);
  const stationFuels = useQuery({
    queryKey: ['stationFuels', target?.placeId],
    queryFn: ({ signal }) => stationsApi.getStationFuels(target.placeId, { signal }),
    enabled: Boolean(placeIdValid),
    staleTime: 0,
    retry: false,
  });
  const entries = useMemo(
    () => Object.fromEntries((stationFuels.data?.fuels ?? []).map((f) => [f.fuelOptionId, f.entry])),
    [stationFuels.data],
  );
  const currentEntry = fuelOptionId ? entries[fuelOptionId] ?? null : null;

  // Prefill the price with the station's current value until the admin types their own
  useEffect(() => {
    if (priceTouched) return;
    const current = currentEntry?.latestPrice;
    setPrice(current != null ? String(current) : '');
  }, [currentEntry, priceTouched]);

  const validate = (next = { target, fuelOptionId, price }) => {
    const found = {};
    const stationError = next.target ? validatePlaceId(next.target.placeId) : 'Select a station or paste its Place ID.';
    if (stationError) found.placeId = stationError;
    if (!next.fuelOptionId) found.fuelOptionId = 'Choose a fuel.';
    const priceError = validatePrice(next.price);
    if (priceError) found.price = priceError;
    return found;
  };

  const change = (patch) => {
    setDirty(true);
    setGeneral([]);
    const next = { target, fuelOptionId, price, ...patch };
    if (attempted) setErrors(validate(next));
  };

  const save = useMutation({
    mutationFn: () => stationsApi.saveStationPrices(target.placeId.trim(), [{ fuelOptionId, latestPrice: parsePrice(price) }]),
    onSuccess: ({ message, data }) => {
      toast.success(message || 'Price saved');
      onSaved(data.placeId, data.fuels);
      onClose();
    },
    onError: (err) => {
      const message = errorMessage(err);
      if (err?.status === 400 && err.fieldErrors?.length) {
        const mapped = mapServerErrors(err.fieldErrors);
        setErrors(mapped.errors);
        setGeneral(mapped.general);
      } else {
        setGeneral([message]);
      }
      toast.error(message);
    },
    onSettled: () => {
      busy.current = false;
    },
  });

  const reset = useMutation({
    mutationFn: () => stationsApi.resetStationFuel(target.placeId, fuelOptionId),
    onSuccess: ({ message, data }) => {
      toast.success(message || 'Reset to unknown');
      setConfirm(null);
      onSaved(data.placeId, data.fuels);
      onClose();
    },
    onError: (err) => {
      setConfirm(null);
      setGeneral([errorMessage(err)]);
      toast.error(errorMessage(err));
    },
  });

  const submit = () => {
    if (busy.current || save.isPending) return;
    setAttempted(true);
    const found = validate();
    setErrors(found);
    setGeneral([]);
    if (Object.keys(found).length) {
      document.getElementById(found.placeId ? 'station-search' : found.fuelOptionId ? 'fuel-subtype' : 'fuel-price')?.focus();
      return;
    }
    busy.current = true;
    save.mutate();
  };

  const requestClose = () => {
    if (save.isPending || reset.isPending) return;
    if (dirty) setConfirm('discard');
    else onClose();
  };

  const optionLabel = (o) => {
    const entry = entries[o.id];
    if (!entry) return optionName(o);
    if (!entry.isAvailable) return `${optionName(o)} — not sold`;
    return entry.latestPrice != null ? `${optionName(o)} — ${formatPrice(entry.latestPrice)}` : `${optionName(o)} — no price`;
  };

  const footer = (
    <>
      {isEdit && currentEntry ? (
        <button
          type="button"
          onClick={() => setConfirm('reset')}
          disabled={save.isPending || reset.isPending}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-error hover:underline disabled:opacity-40 disabled:cursor-not-allowed rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
        >
          <Icon name="restart_alt" size={18} />
          Reset to unknown
        </button>
      ) : (
        <span />
      )}
      <div className="flex gap-2">
        <Button variant="secondary" size="lg" onClick={requestClose} disabled={save.isPending}>
          Cancel
        </Button>
        <Button size="lg" icon={save.isPending ? undefined : 'check_circle'} onClick={submit} disabled={save.isPending || reset.isPending}>
          {save.isPending ? 'Saving…' : 'Submit'}
        </Button>
      </div>
    </>
  );

  const user = getCurrentUser();

  return (
    <>
      <Drawer open title="Add / Edit Fuel Price" subtitle="Saves to the station's latest fuel price" onRequestClose={requestClose} footer={footer}>
        <form
          noValidate
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {general.length > 0 && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-status-alert-border bg-status-alert-bg px-3 py-2.5 text-xs text-status-alert-text">
              <Icon name="error" size={16} />
              <ul>
                {general.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          {isEdit ? (
            <div>
              <span className="block text-xs font-bold text-on-surface mb-1">Target Station (placeId)</span>
              <div className="p-3 rounded-lg bg-surface-container-low border border-outline-variant/30">
                <p className="text-[13px] font-semibold text-on-surface">{station.name ?? 'Name unavailable'}</p>
                {station.address && <p className="text-xs text-on-surface-variant">{station.address}</p>}
                <p className="font-data-mono text-[10px] text-on-surface-variant break-all">{station.placeId}</p>
              </div>
            </div>
          ) : (
            <StationPicker
              value={target}
              error={errors.placeId}
              onChange={(next) => {
                setTarget(next);
                setPriceTouched(false);
                change({ target: next });
              }}
            />
          )}

          {stationFuels.error && placeIdValid && (
            <p className="text-xs text-status-pending-text">Couldn&apos;t load this station&apos;s current prices: {stationFuels.error.message}</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="fuel-category" className="block text-xs font-bold text-on-surface mb-1">
                Fuel Category
              </label>
              <select
                id="fuel-category"
                value={category}
                onChange={(e) => {
                  const first = groups.find((g) => g.key === e.target.value)?.options[0]?.id ?? null;
                  setFuelOptionId(first);
                  setPriceTouched(false);
                  change({ fuelOptionId: first });
                }}
                className={`${field} ${fieldOk}`}
              >
                {groups.map((g) => (
                  <option key={g.key} value={g.key}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="fuel-subtype" className="block text-xs font-bold text-on-surface mb-1">
                Fuel Sub-Type
              </label>
              <select
                id="fuel-subtype"
                value={fuelOptionId ?? ''}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setFuelOptionId(id);
                  setPriceTouched(false);
                  change({ fuelOptionId: id });
                }}
                aria-invalid={Boolean(errors.fuelOptionId) || undefined}
                aria-describedby={errors.fuelOptionId ? 'fuel-subtype-error' : undefined}
                className={`${field} ${errors.fuelOptionId ? fieldBad : fieldOk}`}
              >
                {(groups.find((g) => g.key === category)?.options ?? []).map((o) => (
                  <option key={o.id} value={o.id}>
                    {optionLabel(o)}
                  </option>
                ))}
              </select>
              <FieldError id="fuel-subtype-error">{errors.fuelOptionId}</FieldError>
            </div>
          </div>

          <div>
            <label htmlFor="fuel-price" className="block text-xs font-bold text-on-surface mb-1">
              Latest Price
            </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-data-mono text-sm text-on-surface-variant pointer-events-none">€</span>
                <input
                  ref={priceRef}
                  id="fuel-price"
                  type="number"
                  inputMode="decimal"
                  step="0.001"
                  min="0.001"
                  max={PRICE_MAX}
                  placeholder="0.00"
                  value={price}
                  onChange={(e) => {
                    setPrice(e.target.value);
                    setPriceTouched(true);
                    change({ price: e.target.value });
                  }}
                  aria-invalid={Boolean(errors.price) || undefined}
                  aria-describedby={`fuel-price-unit${errors.price ? ' fuel-price-error' : ''}`}
                  className={`${field} pl-8 font-data-mono font-bold text-base ${errors.price ? fieldBad : fieldOk}`}
                />
              </div>
              <span id="fuel-price-unit" className="h-10 px-3 inline-flex items-center rounded-lg bg-surface-container-low font-data-mono text-xs text-on-surface-variant whitespace-nowrap">
                {unitShort(option?.unit)}
              </span>
            </div>
            <FieldError id="fuel-price-error">{errors.price}</FieldError>
            {currentEntry && !currentEntry.isAvailable && (
              <p className="mt-1.5 text-xs text-status-pending-text">This fuel is marked as not sold here. Saving a price marks it as sold again.</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <span className="block text-xs font-bold text-on-surface mb-1">Source</span>
              <div className={`${readOnlyField} flex items-center`}>Manual entry</div>
            </div>
            <div>
              <span className="block text-xs font-bold text-on-surface mb-1">Updated By</span>
              <div className={`${readOnlyField} flex items-center truncate`}>{user.name}</div>
            </div>
          </div>
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
      </Drawer>

      <ConfirmDialog
        open={confirm === 'reset'}
        title={`Reset ${option ? optionName(option) : 'this fuel'} to unknown?`}
        description="This deletes Pompi's price and 'not sold' flag for this fuel at this station."
        confirmLabel="Reset to unknown"
        busy={reset.isPending}
        onConfirm={() => reset.mutate()}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'discard'}
        title="Discard unsaved changes?"
        description="The price you entered will be lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        onConfirm={() => {
          setConfirm(null);
          onClose();
        }}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
}
